import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import AnnotatedText from '../AnnotatedText/AnnotatedText';
import Button from '../Button/Button';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import { useToast } from '../../../context/ToastContext';
import {
  getSubmissionComments,
  addSubmissionComment,
  resolveSubmissionComment,
} from '../../../services/compMatcherService';
import { reviewerEditSubmission, allowResubmission } from '../../../services/competitionWorkflowService';
import styles from './SubmissionReviewModal.module.css';

/**
 * Full-screen review of one competition submission, with anchored comments.
 *
 * Full-screen rather than a dialog because the thing being reviewed is a piece
 * of written work — a reviewer reading three paragraphs through a 400px-tall
 * box will skim it, and skimming is how vague feedback gets written.
 *
 * Comments anchor to a text selection. Where the student has since redrafted,
 * the anchor no longer matches what it quoted; those comments are shown in the
 * sidebar marked as out of date rather than highlighted over whatever text now
 * occupies those offsets.
 */
const SubmissionReviewModal = ({
  submission,
  open,
  onClose,
  onRequestChanges,
  onApprove,
  onEdited,
  onReopened,
  canReopen = false,
  readOnly = false,
}) => {
  const toast = useToast();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showResolved, setShowResolved] = useState(false);
  const [draft, setDraft] = useState(null); // { start, end, text }
  const [body, setBody] = useState('');
  const [activeId, setActiveId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editBody, setEditBody] = useState('');
  const [saving, setSaving] = useState(false);

  const submissionId = submission?.id;
  // While editing, the textarea is the source of truth; `local` holds a saved
  // edit so the document re-renders without waiting for the parent to refetch.
  const [local, setLocal] = useState(null);
  const content = local !== null ? local : (submission?.content || '');
  const isApproved = submission?.status === 'approved';
  const canEdit = !readOnly && !isApproved;

  const load = useCallback(async () => {
    if (!submissionId) return;
    try {
      const data = await getSubmissionComments(submissionId, showResolved);
      setComments(data.comments || []);
    } catch (error) {
      toast.error('Could not load comments: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [submissionId, showResolved, toast]);

  useEffect(() => {
    if (open) {
      setLoading(true);
      load();
    }
  }, [open, load]);

  // A reopened modal must not inherit the previous submission's edit state.
  useEffect(() => {
    setEditing(false);
    setEditBody('');
    setLocal(null);
  }, [submissionId, open]);

  // Escape closes, but not while a comment is half-written — losing typed text
  // to a stray keypress is the kind of thing people do not forgive.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (draft || body) {
        setDraft(null);
        setBody('');
        return;
      }
      // Same reasoning as a half-written comment: do not discard a half-typed
      // edit to the student's work on a stray keypress.
      if (editing) return;
      onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, draft, body, editing, onClose]);

  // The review modal underneath keeps its own scroll position while this is
  // open; without this the wheel scrolls that instead of the document being
  // reviewed, since the two now sit in unrelated parts of the DOM.
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  if (!open || !submission) return null;

  const submit = async () => {
    if (!body.trim()) return;
    setBusy(true);
    try {
      await addSubmissionComment(submissionId, {
        body,
        anchorStart: draft?.start ?? null,
        anchorEnd: draft?.end ?? null,
      });
      setBody('');
      setDraft(null);
      await load();
    } catch (error) {
      toast.error('Could not save that comment: ' + error.message);
    } finally {
      setBusy(false);
    }
  };

  const toggleResolved = async (comment) => {
    try {
      await resolveSubmissionComment(comment.id, !comment.resolved_at);
      await load();
    } catch (error) {
      toast.error('Could not update that comment: ' + error.message);
    }
  };

  const reopen = async () => {
    setSaving(true);
    try {
      const updated = await allowResubmission(submissionId);
      toast.success('Reopened — the student can submit this again.');
      onReopened?.(updated);
      onClose?.();
    } catch (error) {
      toast.error('Could not reopen: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const startEdit = () => {
    setEditBody(content);
    setDraft(null);
    setBody('');
    setEditing(true);
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const updated = await reviewerEditSubmission(submissionId, { content: editBody });
      setLocal(updated.content ?? editBody);
      setEditing(false);
      toast.success('Submission updated');
      // Comment anchors are character offsets into the old text, so any that
      // no longer match come back marked out of date — refetch rather than
      // leaving stale highlights over rewritten text.
      load();
      onEdited?.(updated);
    } catch (error) {
      toast.error('Could not save: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const openCount = comments.filter((c) => !c.resolved_at).length;

  // Portalled to <body> deliberately. This opens from inside the shared Modal,
  // whose overlay sets `backdrop-filter` and animates `transform` — either one
  // makes it the containing block for `position: fixed` descendants and traps
  // them in its stacking context. Rendered in place, this "full-screen" surface
  // is sized against that modal's box instead of the viewport and no z-index
  // can lift it above the dialog underneath, which is exactly how it ended up
  // looking transparent with the review modal showing through the text.
  return createPortal(
    <div className={styles.overlay} role="dialog" aria-modal="true">
      <header className={styles.header}>
        <div>
          <h2 className={styles.title}>{submission.name || 'Submission'}</h2>
          <span className={styles.meta}>
            {submission.status}
            {submission.word_count ? ` · ${submission.word_count} words` : ''}
            {openCount ? ` · ${openCount} open comment${openCount === 1 ? '' : 's'}` : ''}
          </span>
        </div>
        <div className={styles.headerActions}>
          {canReopen && !editing && !readOnly && (
            <Button variant="outline" size="small" onClick={reopen} loading={saving}>
              Allow resubmission
            </Button>
          )}
          {canEdit && !editing && (
            <Button variant="outline" size="small" onClick={startEdit}>
              Edit submission
            </Button>
          )}
          {editing && (
            <>
              <Button size="small" onClick={saveEdit} loading={saving} disabled={saving}>
                Save changes
              </Button>
              <button
                className={styles.cancel}
                onClick={() => {
                  setEditing(false);
                  setEditBody('');
                }}
              >
                Cancel
              </button>
            </>
          )}
          {!editing && !readOnly && onRequestChanges && (
            <Button variant="outline" size="small" onClick={() => onRequestChanges(comments)}>
              Request changes
            </Button>
          )}
          {!editing && !readOnly && onApprove && (
            <Button size="small" onClick={onApprove}>
              Approve
            </Button>
          )}
          <button className={styles.close} onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
      </header>

      <div className={styles.body}>
        <main className={styles.document}>
          {submission.link_url && (
            <p className={styles.link}>
              Link submitted:{' '}
              <a href={submission.link_url} target="_blank" rel="noreferrer">
                {submission.link_url}
              </a>
            </p>
          )}
          {submission.file_name && (
            <p className={styles.link}>File submitted: {submission.file_name}</p>
          )}
          {editing ? (
            <>
              <textarea
                className={styles.editor}
                value={editBody}
                onChange={(e) => setEditBody(e.target.value)}
                autoFocus
                spellCheck
              />
              <p className={styles.editHint}>
                You are editing this student's submission directly.
                {submission.word_count != null && (
                  <> Currently {editBody.trim() ? editBody.trim().split(/\s+/).length : 0} words.</>
                )}
              </p>
            </>
          ) : (
            <AnnotatedText
              text={content}
              comments={comments}
              activeId={activeId}
              readOnly={readOnly}
              onSelect={(selection) => {
                setDraft(selection);
                setActiveId(null);
              }}
              onCommentClick={(comment) => setActiveId(comment.id)}
            />
          )}
        </main>

        <aside className={styles.sidebar}>
          <div className={styles.sidebarHead}>
            <span className={styles.sidebarTitle}>Comments</span>
            <label className={styles.showResolved}>
              <input
                type="checkbox"
                checked={showResolved}
                onChange={(e) => setShowResolved(e.target.checked)}
              />
              Show resolved
            </label>
          </div>

          {(draft || (!readOnly && body)) && (
            <div className={styles.draft}>
              {draft && <blockquote className={styles.quote}>“{draft.text}”</blockquote>}
              <textarea
                className={styles.input}
                autoFocus
                rows={3}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="What needs to change here?"
              />
              <div className={styles.draftActions}>
                <Button size="small" onClick={submit} loading={busy} disabled={!body.trim()}>
                  Comment
                </Button>
                <button
                  className={styles.cancel}
                  onClick={() => {
                    setDraft(null);
                    setBody('');
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {!readOnly && !draft && !body && (
            <p className={styles.hint}>
              Select any part of the text to comment on it, or{' '}
              <button className={styles.linkButton} onClick={() => setBody(' ')}>
                comment on the whole submission
              </button>
              .
            </p>
          )}

          {loading ? (
            <LoadingSpinner size="small" message="Loading comments..." />
          ) : comments.length === 0 ? (
            <p className={styles.hint}>No comments yet.</p>
          ) : (
            comments.map((comment) => (
              <div
                key={comment.id}
                className={`${styles.comment} ${
                  activeId === comment.id ? styles.commentActive : ''
                } ${comment.resolved_at ? styles.commentResolved : ''}`}
                onClick={() => setActiveId(comment.id)}
              >
                <div className={styles.commentHead}>
                  <span className={styles.author}>{comment.author_name || 'Reviewer'}</span>
                  <button
                    className={styles.resolve}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleResolved(comment);
                    }}
                  >
                    {comment.resolved_at ? 'Reopen' : 'Resolve'}
                  </button>
                </div>
                {comment.quoted_text && (
                  <blockquote className={styles.quote}>“{comment.quoted_text}”</blockquote>
                )}
                {comment.orphaned && (
                  <p className={styles.orphaned}>
                    The text this referred to has changed since it was written.
                  </p>
                )}
                <p className={styles.commentBody}>{comment.body}</p>
              </div>
            ))
          )}
        </aside>
      </div>
    </div>,
    document.body
  );
};

export default SubmissionReviewModal;
